import React from 'react';
import { Box, Paper, TextField } from '@mui/material';
import { Controller, FieldPath, useFormContext } from 'react-hook-form';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import { Feature, defaultCurrencyImage } from '@loyalty/shared-common';
import { QlfRte } from '@qualifio/ui-commons/dist/components/qlf-rte';
import { QlfTitle, colorPalette } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { ExpirationForm } from '../../../expiration/expiration-form';
import { LanguageTabs } from '../../../shared/language-tabs';
import { UploadableImage } from '../../../shared/image';
import { onQlfRteChange } from '../../../shared/forms/on-qlf-rte-change';
import { useCurrencies } from '../../CurrenciesContext';
import { useFeatures } from '../../../features/FeaturesContext';
import { useProgram } from '../../../loyalty/ProgramContext';

export function CurrencyEdition(): React.ReactElement {
  const { t } = useTranslation();
  const { program } = useProgram();
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
  } = useFormContext<CurrencyAttributes>();
  const { selectedCurrency } = useCurrencies();
  const { features } = useFeatures();
  const { write: currencyWrite } = features[Feature.CURRENCY];

  const programId = program.id;

  const handleUploadError = (name: FieldPath<CurrencyAttributes>, message?: string) => {
    if (message) {
      setError(name, { message });
    } else {
      clearErrors(name);
    }
  };

  return (
    <div>
      <QlfTitle variant="h2" separator={{ state: 'left' }}>
        {t('currencies.edition.title')}
      </QlfTitle>
      <LanguageTabs
        languages={program.languages}
        message={t('currencies.edition.message')}
        errors={errors?.i18nAttributes}
      >
        {({ language }) => (
          <Box key={language} sx={{ display: 'flex' }}>
            <Controller
              control={control}
              name={`i18nAttributes.${language}.image`}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <UploadableImage
                  {...field}
                  disabled={!currencyWrite || !!selectedCurrency?.deletedAt}
                  programId={programId}
                  type="currency"
                  errorMessage={error?.message}
                  onError={(name, message) =>
                    handleUploadError(name as FieldPath<CurrencyAttributes>, message)
                  }
                >
                  <img src={defaultCurrencyImage} alt="" />
                </UploadableImage>
              )}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                padding: 0,
                paddingLeft: 2,
              }}
            >
              <Controller
                control={control}
                name={`i18nAttributes.${language}.name`}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    disabled={!currencyWrite || !!selectedCurrency?.deletedAt}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    placeholder={t('currencies.edition.placeholders.name')}
                    id="currency-name-textfield-id"
                  />
                )}
              />
              <Controller
                control={control}
                name={`i18nAttributes.${language}.description`}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <QlfRte
                    {...field}
                    onChange={onQlfRteChange(
                      field.onChange,
                      () => getValues().i18nAttributes?.[language]?.description,
                    )}
                    disabled={!currencyWrite || !!selectedCurrency?.deletedAt}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    label={t('currencies.edition.placeholders.description')}
                    rows={5}
                    multiline
                  />
                )}
              />
            </Box>
          </Box>
        )}
      </LanguageTabs>
      <Paper
        sx={{
          backgroundColor: colorPalette.grey.light,
          marginTop: 1.5,
          padding: 2,
        }}
      >
        <ExpirationForm kind={'currency'} />
      </Paper>
    </div>
  );
}
